/**
 * Universal Session Recording SDK v1.0
 * Records user sessions including clicks, mouse movements, scrolls, and interactions
 * Sends data as type: "sessionrecord"
 */

(function(window, document) {
  'use strict';

  // Configuration
  const CONFIG = {
    apiEndpoint: 'https://cvm-api.worktual.co.uk/cvm_api_service/v1/insert_tracking_nfspath',
    domainId: 20556,
    domainName: window.location.hostname,
    autoStart: true,
    maxEvents: 2000,
    sampleRate: 0.1, // Mouse movement sampling (10%)
    batchInterval: 60000, // Send every 60 seconds
    debug: false,

    // Privacy settings
    maskSensitiveData: true,
    recordPasswords: false,
    recordEmails: false,
  };

  // Session state
  const STATE = {
    visitorId: null,
    sessionId: null,
    isRecording: false,
    startTime: 0,
    events: [],
    batchTimer: null,
    observers: {
      mutation: null,
      performance: null,
    },
    handlers: {},
  };

  // Utility: Generate unique ID
  function generateId(prefix) {
    return prefix + '_' + Math.random().toString(36).substring(2, 11) + Date.now().toString(36);
  }

  // Utility: Get/Set Cookie
  function getCookie(name) {
    const cookies = document.cookie ? document.cookie.split('; ') : [];
    const cookie = cookies.find(row => row.startsWith(name + '='));
    return cookie ? decodeURIComponent(cookie.split('=')[1]) : null;
  }

  function setCookie(name, value, days) {
    const expires = new Date(Date.now() + days * 864e5).toUTCString();
    document.cookie = name + '=' + encodeURIComponent(value) + '; path=/; expires=' + expires + '; SameSite=Lax';
  }

  // Utility: Check consent
  function isConsentMissing() {
    const consent = getCookie('CK_Cons') || localStorage.getItem('cookieConsent');
    return !consent;
  }

  // Initialize Visitor ID
  function initVisitorId() {
    let visitorId = getCookie('visitor_id');
    if (!visitorId) {
      visitorId = generateId('visitor');
      setCookie('visitor_id', visitorId, 365);
    }
    STATE.visitorId = visitorId;
    return visitorId;
  }

  // Initialize Session ID
  function initSessionId() {
    let sessionId = sessionStorage.getItem('session_recording_id');
    if (!sessionId) {
      sessionId = generateId('session');
      sessionStorage.setItem('session_recording_id', sessionId);
    }
    STATE.sessionId = sessionId;
    return sessionId;
  }

  // Helper: Get element selector
  function getElementSelector(element) {
    if (!element || !element.tagName) return '';

    if (element.id) return '#' + element.id;

    if (element.className && typeof element.className === 'string') {
      const classes = element.className.trim().split(/\s+/).slice(0, 2).join('.');
      if (classes) return '.' + classes;
    }

    return element.tagName.toLowerCase();
  }

  // Helper: Sanitize text (remove sensitive data)
  function sanitizeText(text) {
    if (!CONFIG.maskSensitiveData || !text) return text;

    let sanitized = text;

    // Mask emails
    if (!CONFIG.recordEmails) {
      sanitized = sanitized.replace(/\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/g, '[EMAIL]');
    }

    // Mask credit cards
    sanitized = sanitized.replace(/\b\d{4}[\s-]?\d{4}[\s-]?\d{4}[\s-]?\d{4}\b/g, '[CARD]');

    // Mask SSN
    sanitized = sanitized.replace(/\b\d{3}-\d{2}-\d{4}\b/g, '[SSN]');

    return sanitized;
  }

  // Record event
  function recordEvent(type, data) {
    if (!STATE.isRecording) return;

    const event = {
      type: type,
      data: data,
      timestamp: Date.now() - STATE.startTime,
      absoluteTimestamp: Date.now(),
    };

    STATE.events.push(event);

    // Limit events array size
    if (STATE.events.length > CONFIG.maxEvents) {
      STATE.events = STATE.events.slice(-Math.floor(CONFIG.maxEvents * 0.8));
    }

    if (CONFIG.debug) {
      console.log('Session event recorded:', event);
    }
  }

  // Record initial state
  function recordInitialState() {
    recordEvent('session_start', {
      url: window.location.href,
      referrer: document.referrer,
      title: document.title,
      viewport: {
        width: window.innerWidth,
        height: window.innerHeight,
      },
      screen: {
        width: screen.width,
        height: screen.height,
        colorDepth: screen.colorDepth,
      },
      userAgent: navigator.userAgent,
      language: navigator.language,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      timestamp: 0,
    });
  }

  // Event Handlers
  const EventHandlers = {
    click: function(event) {
      const target = event.target;
      recordEvent('click', {
        x: event.clientX,
        y: event.clientY,
        pageX: event.pageX,
        pageY: event.pageY,
        target: getElementSelector(target),
        tagName: target.tagName,
        text: sanitizeText(target.innerText?.substring(0, 100) || ''),
        href: target.href || null,
        button: event.button,
      });
    },

    mousemove: function(event) {
      if (Math.random() > CONFIG.sampleRate) return; // Sample rate

      recordEvent('mousemove', {
        x: event.clientX,
        y: event.clientY,
        pageX: event.pageX,
        pageY: event.pageY,
      });
    },

    scroll: function() {
      recordEvent('scroll', {
        x: window.scrollX,
        y: window.scrollY,
        maxX: document.documentElement.scrollWidth - window.innerWidth,
        maxY: document.documentElement.scrollHeight - window.innerHeight,
        depth: getScrollDepth(),
      });
    },

    input: function(event) {
      const target = event.target;

      // Skip password fields
      if (!CONFIG.recordPasswords && target.type === 'password') return;

      let value = target.value;

      // Mask sensitive inputs
      if (CONFIG.maskSensitiveData) {
        if (target.type === 'password') return;
        if (target.type === 'email' && !CONFIG.recordEmails) value = '[EMAIL]';
        if (target.type === 'text') value = '[REDACTED]';
      }

      recordEvent('input', {
        target: getElementSelector(target),
        type: target.type,
        name: target.name,
        value: value?.substring(0, 10),
        tagName: target.tagName,
      });
    },

    keydown: function(event) {
      const allowedKeys = ['Enter', 'Tab', 'Escape', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'];
      if (!allowedKeys.includes(event.key)) return;

      recordEvent('keypress', {
        key: event.key,
        target: getElementSelector(event.target),
        ctrlKey: event.ctrlKey,
        altKey: event.altKey,
        shiftKey: event.shiftKey,
      });
    },

    focus: function(event) {
      const target = event.target;
      recordEvent('focus', {
        target: getElementSelector(target),
        tagName: target.tagName,
        type: target.type || null,
      });
    },

    blur: function(event) {
      const target = event.target;
      recordEvent('blur', {
        target: getElementSelector(target),
        tagName: target.tagName,
      });
    },

    resize: function() {
      recordEvent('resize', {
        width: window.innerWidth,
        height: window.innerHeight,
      });
    },

    hashchange: function() {
      recordEvent('hashchange', {
        oldURL: document.referrer,
        newURL: window.location.href,
        hash: window.location.hash,
      });
    },

    visibilitychange: function() {
      recordEvent('visibility_change', {
        hidden: document.hidden,
        visibilityState: document.visibilityState,
      });
    },

    beforeunload: function() {
      recordEvent('beforeunload', {
        url: window.location.href,
        duration: Date.now() - STATE.startTime,
      });

      // Send final data
      sendSessionData();
    },

    // Video/Audio event handlers
    videoplay: function(event) {
      const target = event.target;
      recordEvent('video_play', {
        target: getElementSelector(target),
        tagName: target.tagName,
        src: target.currentSrc || target.src || null,
        currentTime: target.currentTime,
        duration: target.duration,
        volume: target.volume,
        muted: target.muted,
        playbackRate: target.playbackRate,
      });
    },

    videopause: function(event) {
      const target = event.target;
      recordEvent('video_pause', {
        target: getElementSelector(target),
        tagName: target.tagName,
        src: target.currentSrc || target.src || null,
        currentTime: target.currentTime,
        duration: target.duration,
      });
    },

    videoended: function(event) {
      const target = event.target;
      recordEvent('video_ended', {
        target: getElementSelector(target),
        tagName: target.tagName,
        src: target.currentSrc || target.src || null,
        duration: target.duration,
      });
    },

    videoseeking: function(event) {
      const target = event.target;
      recordEvent('video_seeking', {
        target: getElementSelector(target),
        tagName: target.tagName,
        currentTime: target.currentTime,
        duration: target.duration,
      });
    },

    videoseeked: function(event) {
      const target = event.target;
      recordEvent('video_seeked', {
        target: getElementSelector(target),
        tagName: target.tagName,
        currentTime: target.currentTime,
        duration: target.duration,
      });
    },

    videovolumechange: function(event) {
      const target = event.target;
      recordEvent('video_volume_change', {
        target: getElementSelector(target),
        tagName: target.tagName,
        volume: target.volume,
        muted: target.muted,
      });
    },

    videotimeupdate: function(event) {
      const target = event.target;
      // Only record at intervals to avoid spam (every 5 seconds)
      if (!target._lastTimeUpdate || Date.now() - target._lastTimeUpdate > 5000) {
        target._lastTimeUpdate = Date.now();
        recordEvent('video_progress', {
          target: getElementSelector(target),
          tagName: target.tagName,
          currentTime: target.currentTime,
          duration: target.duration,
          percentWatched: target.duration ? Math.round((target.currentTime / target.duration) * 100) : 0,
        });
      }
    },

    videoratechange: function(event) {
      const target = event.target;
      recordEvent('video_rate_change', {
        target: getElementSelector(target),
        tagName: target.tagName,
        playbackRate: target.playbackRate,
      });
    },

    videofullscreenchange: function(event) {
      const target = event.target;
      const isFullscreen = document.fullscreenElement === target ||
                          document.webkitFullscreenElement === target ||
                          document.mozFullScreenElement === target ||
                          document.msFullscreenElement === target;

      recordEvent('video_fullscreen_change', {
        target: getElementSelector(target),
        tagName: target.tagName,
        isFullscreen: isFullscreen,
      });
    },
  };

  // Helper: Get scroll depth
  function getScrollDepth() {
    const scrollTop = window.scrollY;
    const docHeight = document.documentElement.scrollHeight - window.innerHeight;
    return docHeight > 0 ? Math.round((scrollTop / docHeight) * 100 * 100) / 100 : 0;
  }

  // Attach video/audio event listeners to a media element
  function attachMediaListeners(mediaElement) {
    if (mediaElement._sessionRecordingAttached) return; // Already attached

    mediaElement.addEventListener('play', EventHandlers.videoplay);
    mediaElement.addEventListener('pause', EventHandlers.videopause);
    mediaElement.addEventListener('ended', EventHandlers.videoended);
    mediaElement.addEventListener('seeking', EventHandlers.videoseeking);
    mediaElement.addEventListener('seeked', EventHandlers.videoseeked);
    mediaElement.addEventListener('volumechange', EventHandlers.videovolumechange);
    mediaElement.addEventListener('timeupdate', EventHandlers.videotimeupdate);
    mediaElement.addEventListener('ratechange', EventHandlers.videoratechange);

    // Fullscreen events
    mediaElement.addEventListener('fullscreenchange', EventHandlers.videofullscreenchange);
    mediaElement.addEventListener('webkitfullscreenchange', EventHandlers.videofullscreenchange);
    mediaElement.addEventListener('mozfullscreenchange', EventHandlers.videofullscreenchange);
    mediaElement.addEventListener('msfullscreenchange', EventHandlers.videofullscreenchange);

    mediaElement._sessionRecordingAttached = true;
  }

  // Remove video/audio event listeners from a media element
  function removeMediaListeners(mediaElement) {
    if (!mediaElement._sessionRecordingAttached) return;

    mediaElement.removeEventListener('play', EventHandlers.videoplay);
    mediaElement.removeEventListener('pause', EventHandlers.videopause);
    mediaElement.removeEventListener('ended', EventHandlers.videoended);
    mediaElement.removeEventListener('seeking', EventHandlers.videoseeking);
    mediaElement.removeEventListener('seeked', EventHandlers.videoseeked);
    mediaElement.removeEventListener('volumechange', EventHandlers.videovolumechange);
    mediaElement.removeEventListener('timeupdate', EventHandlers.videotimeupdate);
    mediaElement.removeEventListener('ratechange', EventHandlers.videoratechange);

    mediaElement.removeEventListener('fullscreenchange', EventHandlers.videofullscreenchange);
    mediaElement.removeEventListener('webkitfullscreenchange', EventHandlers.videofullscreenchange);
    mediaElement.removeEventListener('mozfullscreenchange', EventHandlers.videofullscreenchange);
    mediaElement.removeEventListener('msfullscreenchange', EventHandlers.videofullscreenchange);

    mediaElement._sessionRecordingAttached = false;
  }

  // Attach listeners to all existing and future video/audio elements
  function attachAllMediaListeners() {
    // Wait a bit for the DOM to settle, then attach to existing media elements
    setTimeout(function() {
      const mediaElements = document.querySelectorAll('video, audio');
      mediaElements.forEach(function(element) {
        attachMediaListeners(element);
      });

      if (CONFIG.debug && mediaElements.length > 0) {
        console.log('Attached media listeners to ' + mediaElements.length + ' elements');
      }
    }, 100);
  }

  // Remove listeners from all video/audio elements
  function removeAllMediaListeners() {
    const mediaElements = document.querySelectorAll('video, audio');
    mediaElements.forEach(function(element) {
      removeMediaListeners(element);
    });
  }

  // Attach event listeners
  function attachEventListeners() {
    document.addEventListener('click', EventHandlers.click);
    document.addEventListener('mousemove', EventHandlers.mousemove);
    document.addEventListener('scroll', EventHandlers.scroll, { passive: true });
    document.addEventListener('keydown', EventHandlers.keydown);
    document.addEventListener('input', EventHandlers.input);
    document.addEventListener('focus', EventHandlers.focus, true);
    document.addEventListener('blur', EventHandlers.blur, true);

    window.addEventListener('resize', EventHandlers.resize);
    window.addEventListener('hashchange', EventHandlers.hashchange);
    window.addEventListener('beforeunload', EventHandlers.beforeunload);

    document.addEventListener('visibilitychange', EventHandlers.visibilitychange);

    // Attach video/audio listeners
    attachAllMediaListeners();

    if (CONFIG.debug) {
      console.log('Session recording event listeners attached');
    }
  }

  // Remove event listeners
  function removeEventListeners() {
    document.removeEventListener('click', EventHandlers.click);
    document.removeEventListener('mousemove', EventHandlers.mousemove);
    document.removeEventListener('scroll', EventHandlers.scroll);
    document.removeEventListener('keydown', EventHandlers.keydown);
    document.removeEventListener('input', EventHandlers.input);
    document.removeEventListener('focus', EventHandlers.focus, true);
    document.removeEventListener('blur', EventHandlers.blur, true);

    window.removeEventListener('resize', EventHandlers.resize);
    window.removeEventListener('hashchange', EventHandlers.hashchange);
    window.removeEventListener('beforeunload', EventHandlers.beforeunload);

    document.removeEventListener('visibilitychange', EventHandlers.visibilitychange);

    // Remove video/audio listeners
    removeAllMediaListeners();
  }

  // Start DOM mutation observer
  function startDOMObserver() {
    if (!window.MutationObserver) return;
    if (!document.body) {
      // Wait for body to be available
      setTimeout(startDOMObserver, 100);
      return;
    }

    STATE.observers.mutation = new MutationObserver(function(mutations) {
      mutations.forEach(function(mutation) {
        if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
          recordEvent('dom_mutation', {
            type: 'childList',
            addedNodes: mutation.addedNodes.length,
            target: getElementSelector(mutation.target),
          });

          // Attach listeners to any newly added video/audio elements
          mutation.addedNodes.forEach(function(node) {
            if (node.nodeType === 1) { // Element node
              // Check if the node itself is a media element
              if (node.tagName === 'VIDEO' || node.tagName === 'AUDIO') {
                attachMediaListeners(node);
                if (CONFIG.debug) {
                  console.log('Attached listeners to dynamically added media element:', node.tagName);
                }
              }
              // Check for media elements within the added node
              if (node.querySelectorAll) {
                const mediaElements = node.querySelectorAll('video, audio');
                mediaElements.forEach(function(element) {
                  attachMediaListeners(element);
                  if (CONFIG.debug) {
                    console.log('Attached listeners to nested media element:', element.tagName);
                  }
                });
              }
            }
          });
        }
      });
    });

    STATE.observers.mutation.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: false,
    });
  }

  // Stop DOM observer
  function stopDOMObserver() {
    if (STATE.observers.mutation) {
      STATE.observers.mutation.disconnect();
      STATE.observers.mutation = null;
    }
  }

  // Start performance observer
  function startPerformanceObserver() {
    if (!window.PerformanceObserver) return;

    try {
      STATE.observers.performance = new PerformanceObserver(function(list) {
        list.getEntries().forEach(function(entry) {
          recordEvent('performance', {
            name: entry.name,
            type: entry.entryType,
            startTime: entry.startTime,
            duration: entry.duration,
          });
        });
      });

      STATE.observers.performance.observe({ entryTypes: ['navigation', 'resource'] });
    } catch (e) {
      if (CONFIG.debug) {
        console.warn('Performance observer not supported:', e);
      }
    }
  }

  // Stop performance observer
  function stopPerformanceObserver() {
    if (STATE.observers.performance) {
      STATE.observers.performance.disconnect();
      STATE.observers.performance = null;
    }
  }

  // Send session data to API
  async function sendSessionData(forceHeartbeat) {
    if (isConsentMissing()) {
      if (CONFIG.debug) console.warn('Consent missing, cannot send session data');
      return;
    }
    if (!CONFIG.domainId) {
      if (CONFIG.debug) console.warn('DomainId not configured for session recording');
      return;
    }

    // If no events and not forced heartbeat, skip sending
    if (STATE.events.length === 0 && !forceHeartbeat) {
      if (CONFIG.debug) console.log('No events to send, skipping...');
      return;
    }

    const sessionData = {
      sessionId: STATE.sessionId,
      visitorId: STATE.visitorId,
      events: STATE.events,
      duration: Date.now() - STATE.startTime,
      url: window.location.href,
      startTime: STATE.startTime,
      endTime: Date.now(),
      userAgent: navigator.userAgent,
      viewport: {
        width: window.innerWidth,
        height: window.innerHeight,
      },
      screen: {
        width: screen.width,
        height: screen.height,
      },
      eventCount: STATE.events.length,
      pageTitle: document.title,
      isHeartbeat: forceHeartbeat && STATE.events.length === 0,
    };

    const payload = {
      domainId: CONFIG.domainId,
      domainName: CONFIG.domainName,
      visitorId: STATE.visitorId,
      type: 'sessionrecord',
      trackingData: sessionData,
    };

    try {
      await fetch(CONFIG.apiEndpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        keepalive: true,
      });

      if (CONFIG.debug) {
        console.log('Session recording sent:', payload);
      }

      // Clear events after sending
      STATE.events = [];

    } catch (error) {
      if (CONFIG.debug) {
        console.error('Failed to send session recording:', error);
      }

      // Fallback to beacon
      if (navigator.sendBeacon) {
        navigator.sendBeacon(CONFIG.apiEndpoint, JSON.stringify(payload));
      }
    }
  }

  // Start batch sending
  function startBatchSending() {
    if (STATE.batchTimer) clearInterval(STATE.batchTimer);

    STATE.batchTimer = setInterval(function() {
      // Always send data every 60 seconds (heartbeat)
      // Pass true to force sending even if no events
      sendSessionData(true);

      if (CONFIG.debug) {
        console.log('Batch interval triggered - sending session data');
      }
    }, CONFIG.batchInterval);

    if (CONFIG.debug) {
      console.log('Batch sending started with interval:', CONFIG.batchInterval, 'ms');
    }
  }

  // Stop batch sending
  function stopBatchSending() {
    if (STATE.batchTimer) {
      clearInterval(STATE.batchTimer);
      STATE.batchTimer = null;
    }
  }

  // Start recording
  function startRecording(options) {
    if (STATE.isRecording) {
      if (CONFIG.debug) console.warn('Session recording already started');
      return;
    }

    if (isConsentMissing()) {
      if (CONFIG.debug) console.warn('Consent missing, session recording disabled');
      return;
    }

    // Merge options
    if (options) {
      Object.assign(CONFIG, options);
    }

    // Initialize
    initVisitorId();
    initSessionId();

    STATE.isRecording = true;
    STATE.startTime = Date.now();
    STATE.events = [];

    // Record initial state
    recordInitialState();

    // Attach listeners
    attachEventListeners();
    startDOMObserver();
    startPerformanceObserver();
    startBatchSending();

    if (CONFIG.debug) {
      console.log('Session recording started', {
        sessionId: STATE.sessionId,
        visitorId: STATE.visitorId,
      });
    }
  }

  // Stop recording
  function stopRecording() {
    if (!STATE.isRecording) return;

    STATE.isRecording = false;

    // Remove listeners
    removeEventListeners();
    stopDOMObserver();
    stopPerformanceObserver();
    stopBatchSending();

    // Send final data
    if (STATE.events.length > 0) {
      sendSessionData();
    }

    if (CONFIG.debug) {
      console.log('Session recording stopped');
    }
  }

  // Pause recording
  function pauseRecording() {
    if (!STATE.isRecording) return;
    recordEvent('session_pause', { timestamp: Date.now() - STATE.startTime });
    removeEventListeners();
    stopBatchSending();

    if (CONFIG.debug) {
      console.log('Session recording paused');
    }
  }

  // Resume recording
  function resumeRecording() {
    if (!STATE.isRecording) return;
    recordEvent('session_resume', { timestamp: Date.now() - STATE.startTime });
    attachEventListeners();
    startBatchSending();

    if (CONFIG.debug) {
      console.log('Session recording resumed');
    }
  }

  // Public API
  window.SessionRecording = {
    // Start recording
    start: function(options) {
      startRecording(options);
    },

    // Stop recording
    stop: function() {
      stopRecording();
    },

    // Pause recording
    pause: function() {
      pauseRecording();
    },

    // Resume recording
    resume: function() {
      resumeRecording();
    },

    // Send data immediately
    sendNow: function() {
      sendSessionData();
    },

    // Get current session data
    getSessionData: function() {
      return {
        sessionId: STATE.sessionId,
        visitorId: STATE.visitorId,
        events: STATE.events,
        duration: STATE.isRecording ? Date.now() - STATE.startTime : 0,
        isRecording: STATE.isRecording,
        eventCount: STATE.events.length,
      };
    },

    // Get session ID
    getSessionId: function() {
      return STATE.sessionId;
    },

    // Get visitor ID
    getVisitorId: function() {
      return STATE.visitorId;
    },

    // Check if recording
    isRecording: function() {
      return STATE.isRecording;
    },

    // Configure
    configure: function(options) {
      Object.assign(CONFIG, options);
    },
  };

  // Auto-initialize if config exists
  function init() {
    if (window.SessionRecordingConfig) {
      if (window.SessionRecordingConfig.autoStart !== false) {
        startRecording(window.SessionRecordingConfig);
      }
    } else if (CONFIG.autoStart) {
      // Auto-start with default config if no config provided
      startRecording();
    }
  }

  // Wait for DOM to be ready before initializing
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    // DOM is already ready
    init();
  }

})(window, document);
